// Panel "Modo Auditor" (2026-09-09) — solo para el admin del HCE. Autochequeo
// (el mismo checklist que va a ver el inspector), generación de acceso
// efímero (QR + PIN, dialog), historial de visitas pasadas y el resumen de
// cumplimiento en PDF (Flujo A de la investigación de UX: revisarlo ANTES de
// que llegue el inspector, no durante).
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import QRCode from 'qrcode';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { leerSesionHceAdmin } from '@/auth/hceAdminSession';
import {
  descargarConstanciaAuditoria,
  descargarResumenCumplimiento,
  generarAccesoAuditoria,
  listarHistorialAuditorias,
  obtenerChecklistAuditoria,
  type AuditoriaHistorialItem,
  type ChecklistAuditoria,
  type ResultadoGenerarAcceso,
} from '@/lib/hceAuditoria';
import { ChecklistItems } from '../auditoria/ChecklistItems';
import { VisorAccesos } from '../auditoria/VisorAccesos';

const VARIANTE_POR_ESTADO_HISTORIAL = { activa: 'pine', cerrada: 'sage', expirada: 'clay' } as const;

export function AuditoriaPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sesion = leerSesionHceAdmin();

  const [checklist, setChecklist] = useState<ChecklistAuditoria | null>(null);
  const [cargandoChecklist, setCargandoChecklist] = useState(true);
  const [errorChecklist, setErrorChecklist] = useState<string | null>(null);

  const [historial, setHistorial] = useState<AuditoriaHistorialItem[] | null>(null);
  const [errorHistorial, setErrorHistorial] = useState<string | null>(null);

  const [inspectorNombre, setInspectorNombre] = useState('');
  const [generando, setGenerando] = useState(false);
  const [errorGenerar, setErrorGenerar] = useState<string | null>(null);
  const [acceso, setAcceso] = useState<ResultadoGenerarAcceso | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [dialogAbierto, setDialogAbierto] = useState(false);
  const [linkCopiado, setLinkCopiado] = useState(false);

  const [generandoResumen, setGenerandoResumen] = useState(false);
  const [errorResumen, setErrorResumen] = useState<string | null>(null);

  const cargarChecklist = useCallback(async (token: string) => {
    setCargandoChecklist(true);
    setErrorChecklist(null);
    try {
      setChecklist(await obtenerChecklistAuditoria(token));
    } catch {
      setErrorChecklist(t('admin.auditoria.errorChecklist'));
    } finally {
      setCargandoChecklist(false);
    }
  }, [t]);

  const cargarHistorial = useCallback(async (token: string) => {
    setErrorHistorial(null);
    try {
      setHistorial(await listarHistorialAuditorias(token));
    } catch {
      setErrorHistorial(t('admin.auditoria.errorHistorial'));
    }
  }, [t]);

  useEffect(() => {
    if (!sesion) return;
    void cargarChecklist(sesion.token);
    void cargarHistorial(sesion.token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!sesion) {
    return (
      <div className="min-h-screen bg-hce-paper flex items-center justify-center px-4">
        <Alert variant="red" className="max-w-sm">
          <AlertDescription>{t('admin.usuarios.sesionVencida')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  async function generar(): Promise<void> {
    setGenerando(true);
    setErrorGenerar(null);
    try {
      const resultado = await generarAccesoAuditoria(sesion!.token, inspectorNombre.trim() || undefined);
      setAcceso(resultado);
      setQrDataUrl(await QRCode.toDataURL(resultado.url, { errorCorrectionLevel: 'M' }));
      setLinkCopiado(false);
      setDialogAbierto(true);
      setInspectorNombre('');
      void cargarHistorial(sesion!.token);
    } catch (err) {
      setErrorGenerar(err instanceof Error ? err.message : t('admin.auditoria.errorGenerar'));
    } finally {
      setGenerando(false);
    }
  }

  async function copiarLink(): Promise<void> {
    if (!acceso) return;
    await navigator.clipboard.writeText(acceso.url);
    setLinkCopiado(true);
  }

  async function resumen(): Promise<void> {
    setGenerandoResumen(true);
    setErrorResumen(null);
    try {
      await descargarResumenCumplimiento(sesion!.token);
    } catch {
      setErrorResumen(t('admin.auditoria.errorResumen'));
    } finally {
      setGenerandoResumen(false);
    }
  }

  return (
    <div className="min-h-screen bg-hce-paper px-4 py-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-medium text-hce-ink">{t('admin.auditoria.titulo')}</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => void navigate('/alertas')}>
              {t('admin.alertasVivas.titulo')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void navigate('/comite')}>
              {t('comite.titulo')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void navigate('/usuarios')}>
              {t('admin.auditoria.volver')}
            </Button>
          </div>
        </div>

        <section className="rounded-lg border border-border bg-hce-surface p-4 space-y-3">
          <div>
            <h2 className="text-sm font-medium text-hce-ink">{t('admin.auditoria.generarTitulo')}</h2>
            <p className="text-xs text-hce-muted">{t('admin.auditoria.generarDescripcion')}</p>
          </div>
          {errorGenerar && (
            <Alert variant="red">
              <AlertDescription>{errorGenerar}</AlertDescription>
            </Alert>
          )}
          <div>
            <label className="block text-xs font-mono uppercase tracking-wide text-hce-muted mb-1">{t('admin.auditoria.inspectorNombre')}</label>
            <Input value={inspectorNombre} onChange={(e) => setInspectorNombre(e.target.value)} placeholder={t('admin.auditoria.inspectorNombrePlaceholder')} />
          </div>
          <Button onClick={() => void generar()} disabled={generando} className="w-full">
            {generando ? t('admin.auditoria.generando') : t('admin.auditoria.generarBoton')}
          </Button>
        </section>

        <section className="space-y-2">
          <div>
            <h2 className="text-sm font-medium text-hce-ink">{t('admin.auditoria.autochequeoTitulo')}</h2>
            <p className="text-xs text-hce-muted">{t('admin.auditoria.autochequeoDescripcion')}</p>
          </div>
          {errorChecklist && (
            <Alert variant="red">
              <AlertDescription>{errorChecklist}</AlertDescription>
            </Alert>
          )}
          {cargandoChecklist && !checklist ? (
            <p className="text-sm text-hce-muted">{t('admin.auditoria.cargandoChecklist')}</p>
          ) : checklist ? (
            <ChecklistItems checklist={checklist} token={sesion.token} />
          ) : null}
          <Button variant="outline" onClick={() => void resumen()} disabled={generandoResumen} className="w-full justify-start">
            {generandoResumen ? t('admin.auditoria.resumenGenerando') : t('admin.auditoria.resumenBoton')}
          </Button>
          {errorResumen && (
            <Alert variant="red">
              <AlertDescription>{errorResumen}</AlertDescription>
            </Alert>
          )}
        </section>

        <VisorAccesos token={sesion.token} />

        <section className="space-y-2">
          <h2 className="text-sm font-medium text-hce-ink">{t('admin.auditoria.historialTitulo')}</h2>
          {errorHistorial && (
            <Alert variant="red">
              <AlertDescription>{errorHistorial}</AlertDescription>
            </Alert>
          )}
          {historial && historial.length === 0 ? (
            <p className="text-sm text-hce-muted">{t('admin.auditoria.historialVacio')}</p>
          ) : historial ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin.auditoria.columnas.referencia')}</TableHead>
                  <TableHead>{t('admin.auditoria.columnas.inspector')}</TableHead>
                  <TableHead>{t('admin.auditoria.columnas.creadoPor')}</TableHead>
                  <TableHead>{t('admin.auditoria.columnas.fecha')}</TableHead>
                  <TableHead>{t('admin.auditoria.columnas.estado')}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {historial.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="font-mono text-xs">{h.reference}</TableCell>
                    <TableCell>{h.inspectorNombre || '—'}</TableCell>
                    <TableCell>{h.creadoPorNombre}</TableCell>
                    <TableCell className="text-hce-muted text-xs">{new Date(h.createdAt).toLocaleString('es-EC')}</TableCell>
                    <TableCell>
                      <Badge variant={VARIANTE_POR_ESTADO_HISTORIAL[h.estado]}>
                        {t(`admin.auditoria.estado${h.estado === 'cerrada' ? 'Cerrada' : h.estado === 'activa' ? 'Activa' : 'Expirada'}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {h.tieneConstancia && (
                        <Button variant="outline" size="sm" onClick={() => void descargarConstanciaAuditoria(sesion.token, h.id)}>
                          {t('admin.auditoria.descargarConstancia')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </section>
      </div>

      <Dialog open={dialogAbierto} onOpenChange={setDialogAbierto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.auditoria.qrTitulo')}</DialogTitle>
          </DialogHeader>
          {acceso && qrDataUrl && (
            <div className="flex flex-col items-center gap-3 py-2">
              <img src={qrDataUrl} alt="QR de acceso de auditoría" className="w-56 h-56" />
              <div className="text-center">
                <div className="text-xs font-mono uppercase tracking-wide text-hce-muted">{t('admin.auditoria.pin')}</div>
                <div className="text-2xl font-mono tracking-widest text-hce-ink">{acceso.pin}</div>
              </div>
              <p className="text-xs text-hce-muted">{t('admin.auditoria.vence', { hora: new Date(acceso.exp).toLocaleTimeString('es-EC') })}</p>
              <Button variant="outline" size="sm" onClick={() => void copiarLink()}>
                {linkCopiado ? t('admin.auditoria.linkCopiado') : t('admin.auditoria.copiarLink')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
